import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface Article { readonly articleId: string; readonly articleCode: string; readonly title: string; readonly approvalStatus: string; }
interface Moderation { readonly moderationId: string; readonly entityType: string; readonly entityId: string; readonly actionCode: string; }

/** M27 — Content Review Dashboard (§31). Reviewers approve pending KB articles
 * (BR-02 maker-checker), clear the moderation queue (§23), and run the §21
 * periodic-review / expiry sweep over published articles. */
export function M27ContentReviewDashboardPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [pending, setPending] = useState<Article[]>([]);
  const [queue, setQueue] = useState<Moderation[]>([]);
  const [reviewDue, setReviewDue] = useState<Article[]>([]);
  const [sweepMsg, setSweepMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      apiRequest<{ data: Article[] }>('/v1/collab/knowledge?approvalStatus=SUBMITTED', { token }),
      apiRequest<{ data: Moderation[] }>('/v1/collab/moderation', { token }),
      apiRequest<{ data: Article[] }>('/v1/collab/knowledge/reviews/due', { token }),
    ])
      .then(([a, m, d]) => { if (alive) { setPending(a.data); setQueue(m.data); setReviewDue(d.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  useEffect(() => load(), [load]);

  async function approve(id: string): Promise<void> {
    try {
      await apiRequest(`/v1/collab/knowledge/${id}/approve`, { token, method: 'POST' });
      setPending((p) => p.filter((a) => a.articleId !== id));
    } catch (e) { setError((e as Error).message); }
  }

  async function runSweep(): Promise<void> {
    try {
      const r = await apiRequest<{ data: { flagged: number } }>('/v1/collab/knowledge/reviews/flag', { token, method: 'POST' });
      setSweepMsg(t('m27.contentReview.sweepMsg', { count: r.data.flagged }));
      setError(null);
    } catch (e) { setError((e as Error).message); }
  }

  return (
    <main aria-label="content-review">
      <h1>{t('m27.contentReview.heading')}</h1>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m27.contentReview.loading')}</p> : (
        <>
          <section aria-label="pending-articles">
            <h2>{t('m27.contentReview.pendingApproval')}</h2>
            <p aria-label="pending-count">{t('m27.contentReview.pendingCount', { count: pending.length })}</p>
            <table aria-label="pending-table">
              <thead><tr><th>{t('m27.contentReview.colCode')}</th><th>{t('m27.contentReview.colTitle')}</th><th>{t('m27.contentReview.colAction')}</th></tr></thead>
              <tbody>{pending.map((a) => (
                <tr key={a.articleId}><td>{a.articleCode}</td><td>{a.title}</td>
                  <td><button type="button" onClick={() => void approve(a.articleId)}>{t('m27.contentReview.btnApprove')}</button></td></tr>
              ))}</tbody>
            </table>
          </section>
          <section aria-label="review-due">
            <h2>{t('m27.contentReview.reviewDueHeading')}</h2>
            <p aria-label="review-due-count">{t('m27.contentReview.reviewDueCount', { count: reviewDue.length })}</p>
            <button type="button" onClick={() => void runSweep()}>{t('m27.contentReview.btnSweep')}</button>
            {sweepMsg && <p role="status">{sweepMsg}</p>}
            <ul aria-label="review-due-list">{reviewDue.map((a) => <li key={a.articleId}>{a.articleCode} — {a.title}</li>)}</ul>
          </section>
          <section aria-label="moderation-queue">
            <h2>{t('m27.contentReview.moderationQueue')}</h2>
            <p aria-label="queue-count">{t('m27.contentReview.queueCount', { count: queue.length })}</p>
            <ul>{queue.map((m) => <li key={m.moderationId}>{m.entityType}:{m.entityId} — {m.actionCode}</li>)}</ul>
          </section>
        </>
      )}
    </main>
  );
}
